import { motion } from "framer-motion";
import { ArrowUpRight, BadgeCheck, Ruler, FileCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { CTAButton, Marquee, MaskedLines, ParallaxImage, Reveal, SectionTag, EASE } from "../components/ui-bits";
import { IMAGES, MARQUEE_ITEMS, SERVICES, STATS, TRUST_TYPES } from "../content";

function Hero() {
    return (
        <section data-testid="home-hero" className="blueprint-grid relative overflow-hidden border-b border-inkline">
            <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-12 px-6 pb-20 pt-16 md:px-10 lg:grid-cols-12 lg:gap-6 lg:pb-28 lg:pt-24">
                <div className="lg:col-span-7">
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
                        className="mb-8 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-smoke"
                        data-testid="hero-overline"
                    >
                        <span className="inline-block h-2.5 w-2.5 bg-bluebrand" />
                        Los Angeles ADA Signage Co.
                    </motion.p>
                    <MaskedLines
                        lines={["EVERY SIGN.", "EVERY CODE.", "EVERY DOOR."]}
                        className="font-display text-[15vw] font-bold leading-[0.9] tracking-tighter sm:text-7xl lg:text-[6.5rem]"
                        accentIndexes={[1]}
                    />
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: EASE, delay: 0.75 }}
                        className="mt-8 max-w-xl text-base leading-relaxed text-smoke md:text-lg"
                        data-testid="hero-subcopy"
                    >
                        We design, fabricate, and install ADA-compliant signage for Los Angeles buildings — braille,
                        tactile, wayfinding, and Title 24-ready. One vendor. Zero violations.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: EASE, delay: 0.9 }}
                        className="mt-10 flex flex-wrap gap-4"
                    >
                        <CTAButton to="/get-started" variant="blue" testid="hero-cta-quote">
                            Get a compliance quote
                        </CTAButton>
                        <CTAButton to="/how-it-works" variant="ghost" testid="hero-cta-process">
                            How it works
                        </CTAButton>
                    </motion.div>
                </div>

                <div className="relative lg:col-span-5">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.94, y: 40 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        transition={{ duration: 1.1, ease: EASE, delay: 0.5 }}
                        className="relative"
                    >
                        <div className="absolute -left-4 -top-4 h-full w-full bg-volt" aria-hidden="true" />
                        <ParallaxImage
                            src={IMAGES.braille}
                            alt="Close-up of illuminated Grade 2 braille signage"
                            className="relative aspect-[3/4] w-full border border-inkline"
                            speed={0.1}
                        />
                        <div className="float-slow absolute -bottom-6 -left-6 hidden border border-inkline bg-paper p-4 md:block" data-testid="hero-spec-chip">
                            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-smoke">Spec check</p>
                            <p className="mt-1 font-display text-lg font-bold leading-tight">
                                §703.3 — Grade 2
                                <br />
                                Braille, domed
                            </p>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

function TrustBar() {
    return (
        <section data-testid="trust-bar" className="border-b border-inkline bg-paper">
            <div className="mx-auto max-w-[1440px] px-6 py-20 md:px-10">
                <Reveal>
                    <SectionTag>Trusted across LA's most regulated buildings</SectionTag>
                    <div className="flex flex-wrap gap-3">
                        {TRUST_TYPES.map((t, i) => (
                            <motion.span
                                key={t}
                                initial={{ opacity: 0, y: 14 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, ease: EASE, delay: i * 0.06 }}
                                data-testid={`trust-chip-${i}`}
                                className="cursor-default rounded-full border border-inkline px-5 py-2.5 text-sm font-semibold transition-[background-color,color,border-color] duration-300 hover:border-ink hover:bg-ink hover:text-bone"
                            >
                                {t}
                            </motion.span>
                        ))}
                    </div>
                </Reveal>
                <div className="mt-16 grid grid-cols-2 border border-inkline lg:grid-cols-4">
                    {STATS.map((s, i) => (
                        <Reveal key={s.label} delay={i * 0.08} className={`p-8 ${i !== 0 ? "border-l border-inkline max-lg:border-l-0 max-lg:odd:border-l max-lg:[&:nth-child(n+3)]:border-t" : ""} max-lg:[&:nth-child(2)]:border-l`}>
                            <p className="font-display text-4xl font-bold tracking-tighter text-bluebrand md:text-5xl" data-testid={`stat-value-${i}`}>
                                {s.value}
                            </p>
                            <p className="mt-2 text-sm font-semibold text-smoke">{s.label}</p>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

function ServicesBento() {
    return (
        <section data-testid="home-services" className="border-b border-inkline">
            <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-32">
                <Reveal className="mb-14 flex flex-col justify-between gap-6 md:flex-row md:items-end">
                    <div>
                        <SectionTag>What we build</SectionTag>
                        <h2 className="max-w-2xl font-display text-4xl font-bold leading-none tracking-tight md:text-6xl">
                            Six trades.
                            <br />
                            One code-obsessed shop.
                        </h2>
                    </div>
                    <Link
                        to="/services"
                        data-testid="home-services-link"
                        className="group inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-widest text-bluebrand"
                    >
                        All services
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </Link>
                </Reveal>

                <div className="grid grid-cols-1 gap-px border border-inkline bg-[rgb(17_17_17/0.12)] md:grid-cols-12">
                    {SERVICES.map((s, i) => {
                        const spans = ["md:col-span-7", "md:col-span-5", "md:col-span-5", "md:col-span-7", "md:col-span-7", "md:col-span-5"];
                        return (
                            <Reveal key={s.n} delay={(i % 2) * 0.1} className={`${spans[i]} group bg-bone`}>
                                <Link to="/services" data-testid={`service-card-${i}`} className="flex h-full flex-col p-8 md:p-10">
                                    <div className="flex items-start justify-between">
                                        <span className="font-display text-sm font-bold text-smoke">{s.n}</span>
                                        <ArrowUpRight className="h-5 w-5 text-smoke transition-[color,transform] duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-bluebrand" />
                                    </div>
                                    {s.image && (
                                        <div className="mt-6 overflow-hidden border border-inkline">
                                            <img
                                                src={IMAGES[s.image]}
                                                alt={s.title}
                                                loading="lazy"
                                                className="aspect-[16/9] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                                            />
                                        </div>
                                    )}
                                    <h3 className="mt-6 font-display text-2xl font-bold tracking-tight md:text-3xl">{s.title}</h3>
                                    <p className="mt-2 text-sm font-semibold text-bluebrand">{s.blurb}</p>
                                    <p className="mt-3 max-w-md text-sm leading-relaxed text-smoke">{s.desc}</p>
                                    <div className="mt-auto flex flex-wrap gap-2 pt-6">
                                        {s.refs.map((r) => (
                                            <span key={r} className="border border-inkline px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-smoke">
                                                {r}
                                            </span>
                                        ))}
                                    </div>
                                </Link>
                            </Reveal>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

function ComplianceCoverage() {
    const books = [
        {
            icon: BadgeCheck,
            title: "Federal — 2010 ADA Standards",
            body: "The national baseline. §216 defines which signs must comply (scoping); §703 defines how (technical specs): raised characters, Grade 2 braille, mounting heights, contrast, and pictograms.",
            chips: ["§216 Signs", "§703 Signage", "DOJ Enforced"],
        },
        {
            icon: Ruler,
            title: "California — Title 24 / CBC 11B",
            body: "The stricter state layer. CBC Chapter 11B adds geometric restroom symbols, a 70% LRV contrast rule, and tactile exit signage — the details most out-of-state sign shops miss.",
            chips: ["CBC 11B-703", "70% LRV Contrast", "Geometric Symbols"],
        },
        {
            icon: FileCheck,
            title: "Inspection-Ready Documentation",
            body: "Every project ships with an as-built sign schedule and spec sheet your CASp inspector, landlord, or attorney can verify line by line. SB 1186-friendly, always.",
            chips: ["CASp / SB 1186", "As-Built Schedules", "Warranty"],
        },
    ];
    return (
        <section data-testid="compliance-coverage" className="border-b border-inkline bg-ink text-bone">
            <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-32">
                <Reveal>
                    <p className="mb-8 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-bone/60">
                        <span className="inline-block h-2.5 w-2.5 bg-volt" />
                        Compliance coverage
                    </p>
                    <h2 className="max-w-3xl font-display text-4xl font-bold leading-none tracking-tight md:text-6xl">
                        Two rulebooks.
                        <br />
                        <span className="text-volt">Zero excuses.</span>
                    </h2>
                </Reveal>
                <div className="mt-16 grid grid-cols-1 gap-px border border-boneline bg-[rgb(244_244_240/0.16)] lg:grid-cols-3">
                    {books.map((b, i) => (
                        <Reveal key={b.title} delay={i * 0.1} className="bg-ink p-8 md:p-10">
                            <b.icon className="h-8 w-8 text-volt" strokeWidth={1.5} />
                            <h3 className="mt-6 font-display text-xl font-bold tracking-tight md:text-2xl">{b.title}</h3>
                            <p className="mt-4 text-sm leading-relaxed text-bone/70">{b.body}</p>
                            <div className="mt-6 flex flex-wrap gap-2">
                                {b.chips.map((c) => (
                                    <span key={c} className="border border-boneline px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-bone/70">
                                        {c}
                                    </span>
                                ))}
                            </div>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={0.2} className="mt-10">
                    <CTAButton to="/compliance-guide" variant="volt" testid="coverage-cta-guide">
                        Read the compliance guide
                    </CTAButton>
                </Reveal>
            </div>
        </section>
    );
}

function ManifestoTeaser() {
    const steps = [
        { n: "01", t: "Audit", d: "We catalog every sign and flag every violation." },
        { n: "02", t: "Spec", d: "A room-by-room schedule, drawn to code." },
        { n: "03", t: "Install", d: "Fabricated, mounted, and inspection-ready." },
    ];
    return (
        <section data-testid="manifesto-teaser" className="border-b border-inkline">
            <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-32">
                <Reveal>
                    <SectionTag>The process, in three moves</SectionTag>
                </Reveal>
                <div className="grid grid-cols-1 gap-px border border-inkline bg-[rgb(17_17_17/0.12)] md:grid-cols-3">
                    {steps.map((s, i) => (
                        <Reveal key={s.n} delay={i * 0.1} className="group bg-bone p-8 transition-colors duration-500 hover:bg-volt md:p-10">
                            <p className="font-display text-6xl font-bold tracking-tighter text-bluebrand md:text-7xl">{s.n}</p>
                            <h3 className="mt-6 font-display text-2xl font-bold tracking-tight">{s.t}</h3>
                            <p className="mt-3 text-sm leading-relaxed text-smoke group-hover:text-ink/70">{s.d}</p>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={0.15} className="mt-10">
                    <CTAButton to="/how-it-works" variant="ghost" testid="teaser-cta-process">
                        See all five chapters
                    </CTAButton>
                </Reveal>
            </div>
        </section>
    );
}

export default function Home() {
    return (
        <div data-testid="home-page">
            <Hero />
            <Marquee items={MARQUEE_ITEMS} />
            <TrustBar />
            <ServicesBento />
            <ComplianceCoverage />
            <Marquee items={["48-Hour Quotes", "Free Site Walks", "Licensed & Insured", "SoCal Fabricated"]} />
            <ManifestoTeaser />
        </div>
    );
}
